import { Component, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { forkJoin, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import {
  AnimalDetailsResponse,
  AnimalService,
  RvoService,
} from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { RvoVerblijfPlaats } from './rvo-animal.models';
import {
  animalLifeStatusClass,
  animalLifeStatusLabel,
  daysSinceDmyInclusive,
  formatAnimalDisplayId,
  formatMotherAnimalId,
  formatReplacedAnimalId,
  formatRvoDisplayDate,
  genderLabel,
  resolveAnimalLifeStatus,
  speciesLabel,
} from './animal-info.utils';
import { AnimalLookupDialogService } from './animal-lookup-dialog.service';

@Component({
  selector: 'tcc-mono-animal-info-dialog',
  templateUrl: './animal-info-dialog.component.html',
  styleUrls: ['./animal-info-dialog.component.scss']
})
export class AnimalInfoDialogComponent implements OnInit {
  private readonly _animalService = inject(AnimalService);
  private readonly _rvoService = inject(RvoService);
  private readonly _modalCtrl = inject(ModalController);
  private readonly _router = inject(Router);
  private readonly _animalLookupDialog = inject(AnimalLookupDialogService);

  @Input() public countryCode = '';
  @Input() public identificationNumber = '';
  @Input() public animalSpecies = '1';

  public animalData: AnimalDetailsResponse | null = null;
  public loading = true;
  public error: string | null = null;
  public erpExpanded = false;

  protected readonly speciesLabel = speciesLabel;
  protected readonly genderLabel = genderLabel;
  protected readonly formatAnimalDisplayId = formatAnimalDisplayId;
  protected readonly formatMotherAnimalId = formatMotherAnimalId;
  protected readonly formatReplacedAnimalId = formatReplacedAnimalId;
  protected readonly formatRvoDisplayDate = formatRvoDisplayDate;
  protected readonly animalLifeStatusLabel = animalLifeStatusLabel;
  protected readonly animalLifeStatusClass = animalLifeStatusClass;

  public ngOnInit(): void {
    this._loadAnimalDetails();
  }

  public close(): void {
    void this._modalCtrl.dismiss();
  }

  public searchAgain(): void {
    const params = {
      country_code: this.countryCode,
      identification_number: this.identificationNumber,
      animal_species: this.animalSpecies,
    };
    void this._modalCtrl.dismiss().then(() => {
      this._animalLookupDialog.openLookup(params).pipe(take(1)).subscribe();
    });
  }

  public rvoAgeDays(dateDmy: string | null | undefined): number | null {
    return daysSinceDmyInclusive(dateDmy);
  }

  public rvoMutationDays(dateDmy: string | null | undefined): number | null {
    return daysSinceDmyInclusive(dateDmy);
  }

  public get displayId(): string {
    return formatAnimalDisplayId(this.countryCode, this.identificationNumber);
  }

  public get rvoAnimal() {
    return this.animalData?.rvo_animal ?? null;
  }

  public get verblijfPlaatsen(): RvoVerblijfPlaats[] {
    return (this.rvoAnimal?.verblijfPlaatsen ?? []) as RvoVerblijfPlaats[];
  }

  public lifeStatus(info: NonNullable<AnimalDetailsResponse['rvo_animal']>) {
    return resolveAnimalLifeStatus(info.dierEinddatum, info.dierRedenEinde);
  }

  public lifeStatusBannerClass(info: NonNullable<AnimalDetailsResponse['rvo_animal']>): string {
    switch (this.lifeStatus(info)) {
      case 'slaughtered':
        return 'animal-card animal-card--slaughtered';
      case 'dead':
        return 'animal-card animal-card--dead';
      case 'exported':
        return 'animal-card animal-card--exported';
      case 'ended':
        return 'animal-card animal-card--ended';
      default:
        return 'animal-card animal-card--active';
    }
  }

  public openRouteMap(): void {
    if (!this.verblijfPlaatsen.length) {
      return;
    }

    this._animalLookupDialog
      .openRouteMap({
        places: this.verblijfPlaatsen,
        dierLandcode: this.rvoAnimal?.dierLandcode ?? this.countryCode,
        dierLevensnummer: this.rvoAnimal?.dierLevensnummer ?? this.identificationNumber,
      })
      .pipe(take(1))
      .subscribe();
  }

  public onMapClicked(plaatsOrUbn: RvoVerblijfPlaats | string): void {
    this._animalLookupDialog
      .openMapFromVerblijf(plaatsOrUbn)
      .pipe(take(1))
      .subscribe();
  }

  public openRide(): void {
    const rideId = this.animalData?.ride?.id;
    if (!rideId) {
      return;
    }

    void this._modalCtrl.dismiss().then(() => {
      void this._router.navigate(['/app/office/kvs-rides', rideId]);
    });
  }

  public openErpAnimal(): void {
    const animalId = this.animalData?.animal?.id;
    if (!animalId) {
      return;
    }

    void this._modalCtrl.dismiss().then(() => {
      void this._router.navigate(['/app/animals', animalId]);
    });
  }

  public toggleErp(): void {
    this.erpExpanded = !this.erpExpanded;
  }

  public get hasErpData(): boolean {
    return !!(
      this.animalData?.ride
      || this.animalData?.ride_animal
      || this.animalData?.purchase_order
      || this.animalData?.animal
      || this.animalData?.transport_group
    );
  }

  private _loadAnimalDetails(): void {
    this.loading = true;
    this.error = null;
    this.animalData = null;

    const erpRequest = {
      country_code: this.countryCode,
      identification_number: this.identificationNumber,
    };

    forkJoin({
      erp: this._animalService.getAnimalDetails(erpRequest).pipe(
        catchError(() => of(null)),
      ),
      rvo: this._rvoService.getRvoInfo(
        this.countryCode,
        this.identificationNumber,
        this.animalSpecies,
      ).pipe(
        catchError((err: unknown) => of({ __rvoError: this._rvoErrorMessage(err) })),
      ),
    }).subscribe({
      next: ({ erp, rvo }) => {
        const rvoError = rvo && typeof rvo === 'object' && '__rvoError' in rvo
          ? String((rvo as { __rvoError: string }).__rvoError)
          : null;
        const rvoAnimal = rvoError ? null : rvo;

        if (!rvoAnimal && !erp) {
          this.loading = false;
          this.error = rvoError ?? 'Er is een fout opgetreden bij het ophalen van de diergegevens. Probeer het opnieuw.';
          return;
        }

        this.animalData = {
          ...(erp ?? {}),
          rvo_animal: rvoAnimal as AnimalDetailsResponse['rvo_animal'],
          rvo_error: rvoError,
        };
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = 'Er is een fout opgetreden bij het ophalen van de diergegevens. Probeer het opnieuw.';
      },
    });
  }

  private _rvoErrorMessage(err: unknown): string {
    if (err && typeof err === 'object' && 'error' in err) {
      const body = (err as { error?: { message?: string } | string }).error;
      if (typeof body === 'string') {
        return body;
      }
      if (body && typeof body === 'object' && body.message) {
        return body.message;
      }
    }

    return 'RVO diergegevens konden niet worden opgehaald.';
  }
}
